/**
 * DocumentsPage.tsx — everything in a tender, after it has been uploaded.
 *
 * Until now a document vanished the moment you left the upload screen: the
 * hooks to list, re-file and remove one existed and no component called them.
 * This is that screen, and it also carries the "keep adding to a tender" case —
 * portals publish clarifications and errata for weeks after a bundle goes up,
 * so a project has to stay open to new files rather than freezing at creation.
 *
 * Documents are grouped by the folder they arrived in. That grouping is the
 * visible proof the tree survived the upload, and it is the same folder string
 * the classifier used to decide what each file is.
 */
import { Fragment, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { I } from "../../components/icons";
import { AdminOnlyDeleteDialog } from "../../components/ui/AdminOnlyDeleteDialog";
import { useCanDeleteDocument, useDeleteKbDocument, useKbDeletionPreview } from "../../lib/kbApi";
import { KBDeleteDocumentModal } from "../kb/KBDeleteDocumentModal";
import { ApiError, apiErrorMessage } from "../../lib/api";
import { filesFromDataTransfer, folderPathOf } from "../../lib/dropTree";
import {
  DOC_ROLE_LABEL,
  useDeleteDocument,
  useDocumentKinds,
  useProject,
  useProjectDocuments,
  useUpdateDocument,
  useUploadArchive,
  useUploadDocument,
  type ArchiveSkipped,
  type TenderDocRole,
  type TenderDocument,
} from "../../lib/tenderApi";
import { useFilePicker } from "../../lib/useFilePicker";
import { KbApprovalPanel } from "./KbApprovalPanel";

const ROLE_OPTIONS: TenderDocRole[] = [
  "cme",
  "full_price",
  "np",
  "info",
  "regional",
  "drawing",
  "other",
];

function fmtSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${bytes} B`;
}

/** Documents grouped by the folder they arrived in, root first, then A→Z. */
function groupByFolder(docs: TenderDocument[]): [string, TenderDocument[]][] {
  const groups = new Map<string, TenderDocument[]>();
  for (const d of docs) {
    const key = d.folderPath ?? "";
    const bucket = groups.get(key);
    if (bucket) bucket.push(d);
    else groups.set(key, [d]);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === b) return 0;
    if (a === "") return -1;
    if (b === "") return 1;
    return a.localeCompare(b, "it");
  });
}

export function DocumentsPage(): JSX.Element {
  const { projectId = null } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const project = useProject(projectId);
  const documents = useProjectDocuments(projectId);
  const uploadDocument = useUploadDocument();
  const uploadArchive = useUploadArchive();
  const updateDocument = useUpdateDocument();
  const kinds = useDocumentKinds();
  const deleteDocument = useDeleteDocument();
  // `D-41` + `D-42`, Omar 2026-09-06. This screen and the Knowledge Base list are
  // two views of ONE source PDF, and deleting from either performs the same
  // operation. The Knowledge Base half runs from the browser under the signed-in
  // administrator's own token — the frontend already holds one per backend — so
  // no machine principal gains the power to destroy a tenant's content.
  const canDelete = useCanDeleteDocument();
  const deleteKbDoc = useDeleteKbDocument();
  const [refusedDelete, setRefusedDelete] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    filename: string;
    kbDocumentId: string;
  } | null>(null);
  const deletePreview = useKbDeletionPreview(deleteTarget?.kbDocumentId ?? null);

  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<ArchiveSkipped[]>([]);
  const [adding, setAdding] = useState(false);
  const [drag, setDrag] = useState(false);

  /**
   * Synchronous latch against a double-submit.
   *
   * NOT `adding` on its own: that is React state, so it is still false during
   * the window between two fast clicks and both would dispatch. The ref flips
   * in the same tick as the first click.
   */
  const inFlight = useRef(false);

  /**
   * Which (document, field) corrections are in flight. Keyed `id:field`.
   *
   * **This replaces a value-comparing guard that was wrong four times.** Each
   * version closed one hole by adding just enough bookkeeping to open the next:
   * it remembered the last dispatched value forever (so a value the document was
   * later moved back to became unpickable); then it let an earlier request's
   * settlement wipe a newer one's marker; then, comparing markers by value, it
   * still collided whenever two different dispatches carried the same value.
   * Four defects in twenty lines is the design failing, not the fixes.
   *
   * So there is no value comparison here at all — only "is this field busy".
   * Every one of those failures is structurally unreachable rather than
   * guarded against: at most one correction per field can be in flight, and the
   * key is removed unconditionally when it settles.
   *
   * The ref is the correctness guard because it flips in the same tick as the
   * click; `busyEdits` mirrors it into state only so the control can *render*
   * as disabled. Disabling off the mutation's `isPending` alone would not do:
   * that is React state and has the very commit-timing window that made a
   * synchronous latch necessary in the first place.
   *
   * The cost is honest: after changing a document's role you wait one round
   * trip before you can change that same field again.
   */
  const editing = useRef<Set<string>>(new Set());
  const [busyEdits, setBusyEdits] = useState<ReadonlySet<string>>(new Set());

  function editKey(docId: string, field: "role" | "docKind"): string {
    return `${docId}:${field}`;
  }

  /** Send one correction, holding that field's lock for exactly its duration. */
  async function correct(
    doc: TenderDocument,
    field: "role" | "docKind",
    patch: { role?: TenderDocRole; docKind?: string },
  ): Promise<void> {
    if (!projectId) return;
    const key = editKey(doc.id, field);
    if (editing.current.has(key)) return;
    editing.current.add(key);
    setBusyEdits(new Set(editing.current));
    setError(null);
    try {
      await updateDocument.mutateAsync({ id: doc.id, projectId, ...patch });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      editing.current.delete(key);
      setBusyEdits(new Set(editing.current));
    }
  }

  async function addFiles(items: { file: File; folderPath: string }[]): Promise<void> {
    if (!projectId || items.length === 0) return;
    if (inFlight.current) return;
    inFlight.current = true;
    setAdding(true);
    setError(null);
    setSkipped([]);
    const collected: ArchiveSkipped[] = [];
    const failed: string[] = [];
    try {
      for (const { file, folderPath } of items) {
        try {
          if (/\.zip$/i.test(file.name)) {
            const res = await uploadArchive.mutateAsync({ projectId, file });
            collected.push(...res.skipped);
          } else {
            await uploadDocument.mutateAsync({
              projectId,
              file,
              folderPath: folderPath || undefined,
            });
          }
        } catch (e) {
          failed.push(`${file.name}: ${apiErrorMessage(e)}`);
        }
      }
      setSkipped(collected);
      if (failed.length > 0) setError(`Non caricati — ${failed.join(" · ")}`);
    } finally {
      inFlight.current = false;
      setAdding(false);
    }
  }

  const filePicker = useFilePicker({
    multiple: true,
    onFiles: (files) => void addFiles(files.map((f) => ({ file: f, folderPath: folderPathOf(f) }))),
  });
  const folderPicker = useFilePicker({
    multiple: true,
    directory: true,
    onFiles: (files) => void addFiles(files.map((f) => ({ file: f, folderPath: folderPathOf(f) }))),
  });

  async function onChangeRole(doc: TenderDocument, role: TenderDocRole): Promise<void> {
    if (role === doc.role) return;
    await correct(doc, "role", { role });
  }

  /**
   * Correct what KIND of document this is.
   *
   * This is the control that clears the review flag: `needs_review` is a
   * statement about the kind, so only a kind correction can answer it. Changing
   * the pipeline role deliberately leaves the flag alone — otherwise a document
   * would leave the review queue with its actual misclassification untouched.
   */
  async function onChangeKind(doc: TenderDocument, docKind: string): Promise<void> {
    if (docKind === doc.docKind) return;
    await correct(doc, "docKind", { docKind });
  }

  /**
   * `D-42` — one source PDF, one deletion.
   *
   * The Knowledge Base copy goes FIRST, and the order is load-bearing. If the
   * Knowledge Base call fails, nothing has been lost and the row is still here to
   * try again. If the project call failed first, the summaries would already be
   * gone while the file still sat on this screen — the state a person is least
   * able to make sense of, and the one with no way back.
   *
   * A retry after a partial failure is safe: the Knowledge Base delete answers
   * 404 for a document already gone, which this treats as success, because
   * "it is not there" is the outcome being asked for.
   */
  async function onDelete(doc: TenderDocument): Promise<void> {
    if (!projectId) return;
    setError(null);
    try {
      if (doc.kbDocumentId) {
        try {
          await deleteKbDoc.mutateAsync(doc.kbDocumentId);
        } catch (e) {
          if (!(e instanceof ApiError && e.status === 404)) throw e;
        }
      }
      await deleteDocument.mutateAsync({ id: doc.id, projectId });
      setDeleteTarget(null);
    } catch (e) {
      // The dialog stays open on failure, for the reason the Knowledge Base list
      // records: closing it leaves the person looking at a list that still
      // contains the document with no way back to the confirm.
      setError(apiErrorMessage(e));
    }
  }

  function onRequestDelete(doc: TenderDocument): void {
    if (!canDelete) {
      setRefusedDelete(doc.filename);
      return;
    }
    if (doc.kbDocumentId) {
      // Forwarded to the Knowledge Base: the full confirmation, naming every
      // summary that is about to go. Same dialog as the Knowledge Base list,
      // because since `D-42` it is the same act.
      setDeleteTarget({ id: doc.id, filename: doc.filename, kbDocumentId: doc.kbDocumentId });
      return;
    }
    // Never forwarded — a computo, a price list, a drawing. There are no
    // summaries, and saying so is better than a dialog that lists none.
    if (
      window.confirm(
        `Rimuovere «${doc.filename}» dal progetto?\n\n` +
          "Questo file non è stato inviato alla Knowledge Base, quindi non esistono " +
          "sintesi collegate da eliminare.",
      )
    ) {
      void onDelete(doc);
    }
  }

  const docs = documents.data ?? [];
  const grouped = groupByFolder(docs);
  const needingReview = docs.filter((d) => d.needsReview).length;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1100 }}>
      {filePicker.input}
      {folderPicker.input}
      {refusedDelete !== null && (
        <AdminOnlyDeleteDialog subject={refusedDelete} onDismiss={() => setRefusedDelete(null)} />
      )}
      {deleteTarget && (
        <KBDeleteDocumentModal
          filename={deleteTarget.filename}
          preview={deletePreview.data}
          isLoading={deletePreview.isPending}
          error={deletePreview.error}
          isDeleting={deleteDocument.isPending || deleteKbDoc.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => {
            const doc = docs.find((d) => d.id === deleteTarget.id);
            if (doc) void onDelete(doc);
          }}
        />
      )}

      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate("/")}
        style={{ marginBottom: 14 }}
      >
        ← Workspace
      </button>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <div className="t-eyebrow">Documenti del progetto</div>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>
            {project.data?.name ?? "…"}
          </h1>
          <div style={{ fontSize: 12.5, color: "var(--c-ink-3)", marginTop: 4 }}>
            {docs.length} document{docs.length === 1 ? "o" : "i"}
            {needingReview > 0 && (
              <span style={{ color: "var(--c-warn)" }}> · {needingReview} da confermare</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-sm" onClick={filePicker.open} disabled={adding}>
            <I.Upload size={13} /> Aggiungi file
          </button>
          <button className="btn btn-sm" onClick={folderPicker.open} disabled={adding}>
            <I.Folder size={13} /> Aggiungi cartella
          </button>
        </div>
      </div>

      {/* Drop target for topping the tender up later. */}
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          if (!adding) setDrag(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (adding) return;
          const dt = e.dataTransfer;
          void filesFromDataTransfer(dt).then((dropped) => void addFiles(dropped));
        }}
        style={{
          border: `1.5px dashed ${drag ? "var(--c-brand)" : "var(--c-line)"}`,
          background: drag ? "var(--c-brand-soft)" : "transparent",
          borderRadius: 10,
          padding: "14px 16px",
          textAlign: "center",
          fontSize: 12.5,
          color: "var(--c-ink-3)",
          marginBottom: 16,
          transition: "all var(--tr-fast)",
        }}
      >
        {adding ? (
          "Caricamento in corso…"
        ) : (
          <>
            Trascina qui altri file, cartelle o uno ZIP per aggiungerli a questo progetto.
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 3 }}>
              I bandi pubblicano chiarimenti ed errata anche dopo la consegna.
            </div>
          </>
        )}
      </div>

      {error && (
        <div
          style={{
            fontSize: 12,
            color: "var(--c-err)",
            background: "var(--c-err-soft)",
            borderRadius: 8,
            padding: "8px 10px",
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {skipped.length > 0 && (
        <div
          style={{
            fontSize: 12,
            color: "var(--c-ink-2)",
            background: "var(--c-bg-elev)",
            borderRadius: 8,
            padding: "8px 10px",
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            Non importati dallo ZIP ({skipped.length}):
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {skipped.map((s) => (
              <li key={s.name}>
                <span className="t-mono">{s.name}</span> — {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Above the table, not in front of it: the Knowledge Base gate must
          never block the analysis. That rule survives `P-31`'s reversal of the
          auto-start unchanged — the intake screen now starts both from one
          button, but this page is the LATER route, reached when a run is
          already under way or finished, so nothing here is in front of an
          analysis. It renders nothing at all when a tender has no
          knowledge-base story. */}
      <KbApprovalPanel documents={docs} />

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Documento</th>
              <th style={{ width: 210 }}>Tipo riconosciuto</th>
              <th style={{ width: 200 }}>Ruolo nell&apos;analisi</th>
              <th style={{ width: 90 }}>Dimensione</th>
              <th style={{ width: 46 }} />
            </tr>
          </thead>
          <tbody>
            {documents.isLoading && (
              <tr>
                <td colSpan={5} className="muted" style={{ padding: 20, textAlign: "center" }}>
                  Caricamento…
                </td>
              </tr>
            )}
            {documents.isError && (
              <tr>
                <td colSpan={5} style={{ padding: 20, textAlign: "center", color: "var(--c-err)" }}>
                  {apiErrorMessage(documents.error)}
                </td>
              </tr>
            )}
            {!documents.isLoading && !documents.isError && docs.length === 0 && (
              <tr>
                <td colSpan={5} className="muted" style={{ padding: 20, textAlign: "center" }}>
                  Nessun documento in questo progetto.
                </td>
              </tr>
            )}
            {grouped.map(([folder, rows]) => (
              /* Fragment carries the key: a bare <> cannot take one, and each
                 group renders a header row plus its documents. */
              <Fragment key={folder || "\u0000root"}>
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      background: "var(--c-bg-elev)",
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "var(--c-ink-3)",
                      padding: "6px 12px",
                    }}
                  >
                    <I.Folder size={12} style={{ verticalAlign: -2 }} />{" "}
                    {folder || "Radice del progetto"}
                    <span style={{ fontWeight: 400 }}> · {rows.length}</span>
                  </td>
                </tr>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div style={{ fontSize: 13 }}>{d.filename}</div>
                      <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 1 }}>
                        {/* Both codes when the bundle's index disagrees with the
                            delivery, one when it does not. See IntakeReviewPanel. */}
                        {d.docCode && (
                          <span className="t-mono">
                            {d.declaredDocCode
                              ? `${d.declaredDocCode}_or_${d.docCode}`
                              : d.docCode}{" "}
                          </span>
                        )}
                        {d.docRevision && <span>rev {d.docRevision} </span>}
                        {d.signedBy && (
                          <span title={`Firmato da ${d.signedBy}`}>
                            <I.Check size={10} style={{ verticalAlign: -1 }} /> {d.signedBy}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <select
                        className="input"
                        value={d.docKind}
                        aria-label={`Tipo di ${d.filename}`}
                        disabled={busyEdits.has(editKey(d.id, "docKind")) || kinds.isLoading}
                        onChange={(e) => void onChangeKind(d, e.target.value)}
                        style={{ width: 200, fontSize: 12, padding: "4px 8px" }}
                      >
                        {/* The stored kind is always offered, even if the
                            taxonomy no longer lists it, so the select can never
                            silently show a different document's type. */}
                        {!(kinds.data ?? []).some((k) => k.value === d.docKind) && (
                          <option value={d.docKind}>{d.labelIt || d.docKind}</option>
                        )}
                        {(kinds.data ?? []).map((k) => (
                          <option key={k.value} value={k.value}>
                            {k.labelIt}
                          </option>
                        ))}
                      </select>
                      {/* The reason, not the confidence — see IntakeReviewPanel
                          for why the percentage went. */}
                      {d.needsReview && (
                        <div style={{ fontSize: 11, color: "var(--c-warn)", marginTop: 1 }}>
                          <I.Alert size={10} style={{ verticalAlign: -1 }} /> da confermare
                          {d.reviewReasonIt ? ` — ${d.reviewReasonIt}` : ""}
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        className="input"
                        value={d.role}
                        aria-label={`Ruolo di ${d.filename}`}
                        disabled={busyEdits.has(editKey(d.id, "role"))}
                        onChange={(e) => void onChangeRole(d, e.target.value as TenderDocRole)}
                        style={{ width: 185, fontSize: 12, padding: "4px 8px" }}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {DOC_ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="t-num" style={{ fontSize: 12 }}>
                      {fmtSize(d.sizeBytes)}
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title="Rimuovi documento"
                        aria-label={`Rimuovi ${d.filename}`}
                        disabled={deleteDocument.isPending}
                        onClick={() => onRequestDelete(d)}
                      >
                        <I.Trash size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
